import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { getSuggestedFarmLocation } from '../services/tenantService';
import { Field, Notice } from './patterns';
import { gestureHandlingText } from '../utils/leafletGestureHandling';
import './FarmLocationPicker.css';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Marker.prototype.options.icon = DefaultIcon;

interface GeoPoint {
  type: 'Point';
  coordinates: [number, number];
}

interface FarmLocationPickerProps {
  initialLocation: GeoPoint | null;
  onSave: (location: GeoPoint) => Promise<void>;
}

type Mode = 'orchards' | 'map' | 'manual' | 'current';

interface Suggestion {
  centralLocation: GeoPoint | null;
  source: 'orchards' | 'none';
  orchardCount: number;
}

const DISTANCE_WARN_KM = 5;

const haversineKm = (a: [number, number], b: [number, number]): number => {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const lat1r = toRad(lat1);
  const lat2r = toRad(lat2);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1r) * Math.cos(lat2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const FarmLocationPicker: React.FC<FarmLocationPickerProps> = ({ initialLocation, onSave }) => {
  const [mode, setMode] = useState<Mode | null>(null);
  const [coords, setCoords] = useState<[number, number] | null>(
    initialLocation ? initialLocation.coordinates : null
  );
  const [fromSuggestion, setFromSuggestion] = useState(false);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);

  const [manualLat, setManualLat] = useState('');
  const [manualLon, setManualLon] = useState('');
  const [manualLatErr, setManualLatErr] = useState('');
  const [manualLonErr, setManualLonErr] = useState('');

  const [geoError, setGeoError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    getSuggestedFarmLocation()
      .then(setSuggestion)
      .catch(() => setSuggestion(null));
  }, []);

  const distanceWarning = useMemo(() => {
    if (!coords || !suggestion?.centralLocation || fromSuggestion) return null;
    const dist = haversineKm(coords, suggestion.centralLocation.coordinates);
    return dist > DISTANCE_WARN_KM ? dist : null;
  }, [coords, suggestion, fromSuggestion]);

  const showSuggestButton = suggestion?.source === 'orchards';

  const updateManual = (latStr: string, lonStr: string) => {
    setManualLat(latStr);
    setManualLon(lonStr);
    let latErr = '';
    let lonErr = '';
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (latStr !== '' && (isNaN(lat) || lat < -90 || lat > 90)) {
      latErr = 'Latitude must be between -90 and 90.';
    }
    if (lonStr !== '' && (isNaN(lon) || lon < -180 || lon > 180)) {
      lonErr = 'Longitude must be between -180 and 180.';
    }
    setManualLatErr(latErr);
    setManualLonErr(lonErr);
    if (!latErr && !lonErr && latStr !== '' && lonStr !== '') {
      setCoords([lon, lat]);
      setFromSuggestion(false);
    } else {
      setCoords(null);
    }
  };

  const handleSuggestClick = () => {
    setMode('orchards');
    setGeoError('');
    if (suggestion?.centralLocation) {
      setCoords(suggestion.centralLocation.coordinates);
      setFromSuggestion(true);
    }
  };

  const handleUseCurrentClick = () => {
    setMode('current');
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords([pos.coords.longitude, pos.coords.latitude]);
        setFromSuggestion(false);
      },
      (err) => {
        setGeoError(`Geolocation failed: ${err.message}`);
      }
    );
  };

  const handleMapClick = (lat: number, lon: number) => {
    setCoords([lon, lat]);
    setFromSuggestion(false);
  };

  const handleSave = async () => {
    if (!coords) return;
    setSaving(true);
    setSaveError('');
    try {
      await onSave({ type: 'Point', coordinates: coords });
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const canSave = coords !== null && !saving;

  return (
    <div className="farm-location-picker">
      <p className="farm-location-picker__hint">
        Set the farm's central GPS coordinates. Pick one of the four options below.
      </p>

      <div role="group" aria-label="Farm location picker mode" className="farm-location-picker__modes">
        {showSuggestButton && (
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            aria-pressed={mode === 'orchards'}
            onClick={handleSuggestClick}
          >
            Suggest from orchards
          </button>
        )}
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          aria-pressed={mode === 'map'}
          onClick={() => { setMode('map'); setGeoError(''); }}
        >
          Pick on map
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          aria-pressed={mode === 'manual'}
          onClick={() => { setMode('manual'); setGeoError(''); }}
        >
          Enter manually
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          aria-pressed={mode === 'current'}
          onClick={handleUseCurrentClick}
        >
          Use Current Location
        </button>
      </div>

      {mode === 'orchards' && showSuggestButton && (
        <Notice tone="info" role="alert" className="mb-3">
          <span>
            Using bounding-box centroid of {suggestion?.orchardCount} orchard
            {suggestion?.orchardCount === 1 ? '' : 's'}.
          </span>
        </Notice>
      )}

      {mode === 'map' && (
        <MapPickerView coords={coords} onPick={handleMapClick} />
      )}

      {mode === 'manual' && (
        <div className="farm-location-picker__manual-row mb-3">
          <Field label="Latitude" controlId="farmLocLat" errorText={manualLatErr || undefined}>
            <input
              id="farmLocLat"
              type="number"
              step="any"
              value={manualLat}
              onChange={(e) => updateManual(e.target.value, manualLon)}
            />
          </Field>
          <Field label="Longitude" controlId="farmLocLon" errorText={manualLonErr || undefined}>
            <input
              id="farmLocLon"
              type="number"
              step="any"
              value={manualLon}
              onChange={(e) => updateManual(manualLat, e.target.value)}
            />
          </Field>
        </div>
      )}

      {mode === 'current' && geoError && (
        <Notice tone="danger" className="mb-3">
          <span>{geoError}</span>
        </Notice>
      )}

      {coords && (
        <Notice tone="info" role="alert" className="mb-3">
          <span className="me-3">{`Lat: ${coords[1].toFixed(5)}`}</span>
          <span>{`Lon: ${coords[0].toFixed(5)}`}</span>
        </Notice>
      )}

      {distanceWarning !== null && (
        <Notice tone="warn" className="mb-3">
          <span>
            ⚠️ The picked location is more than 5 km from your orchards
            ({distanceWarning.toFixed(1)} km). Confirm this is correct.
          </span>
        </Notice>
      )}

      {saveError && (
        <Notice tone="danger" className="mb-3">
          <span>{saveError}</span>
        </Notice>
      )}

      <button
        type="button"
        className="pat-btn pat-btn--primary"
        onClick={handleSave}
        disabled={!canSave}
      >
        {saving ? 'Saving…' : 'Save Location'}
      </button>
    </div>
  );
};

interface MapPickerViewProps {
  coords: [number, number] | null;
  onPick: (lat: number, lon: number) => void;
}

const MapPickerView: React.FC<MapPickerViewProps> = ({ coords, onPick }) => {
  const { t } = useTranslation();
  const center: [number, number] = coords ? [coords[1], coords[0]] : [-33.93, 18.84];

  const ClickHandler = () => {
    useMapEvents({
      click: (e: any) => onPick(e.latlng.lat, e.latlng.lng)
    });
    return null;
  };

  return (
    <div className="farm-location-picker__map mb-3">
      <MapContainer center={center} zoom={13} gestureHandling={true} gestureHandlingOptions={gestureHandlingText(t)} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        {coords && (
          <Marker position={[coords[1], coords[0]] as [number, number]} />
        )}
        <ClickHandler />
      </MapContainer>
    </div>
  );
};

export default FarmLocationPicker;
